import { useEffect, useState } from 'react';
import { api, formatApiError, mediaUrl } from '@/lib/api';
import { toast } from 'sonner';
import { useOnline } from '@/lib/useOnline';
import { enqueue, flushQueue } from '@/lib/offline';
import { EmptyState, PriorityChip, Spinner, CarryoverChip, ResolvedChip, TagPill } from '@/components/Common';
import VoiceDictateButton from '@/components/VoiceDictateButton';
import FindingCaptureModal from '@/pages/engagement/FindingCaptureModal';
import { TEST_IDS } from '@/constants/ids';
import {
  PlusIcon,
  TrashIcon,
  SparkleIcon,
  CameraIcon,
  ImageIcon,
  XIcon,
  VideoCameraIcon,
  ListChecksIcon,
  PencilSimpleIcon,
  ArrowsOutSimpleIcon,
} from '@phosphor-icons/react';

const PRIORITIES = ['P1', 'P2', 'P3'];

export default function FindingsTab({ eng, onJumpToDoc, focusFindingId, onFocusConsumed }) {
  const [findings, setFindings] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | finding obj  (full editor)
  const [capture, setCapture] = useState(null); // null | 'new' | finding obj  (v3 capture sheet)
  const [filterPriority, setFilterPriority] = useState(null); // null | 'P1' | 'P2' | 'P3'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'open' | 'resolved' | 'carryover'
  const [filterCategory, setFilterCategory] = useState(null); // null | category string
  const [highlightFindingId, setHighlightFindingId] = useState(null);
  const online = useOnline();

  async function load() {
    try {
      const { data } = await api.get(`/engagements/${eng.id}/findings`);
      setFindings(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setFindings([]);
    }
  }
  useEffect(() => {
    load();
  }, [eng.id]);

  // GL-SPEC-APP-019 — deep-link from Docs tab: clear filters so the target is
  // visible, then scroll-and-highlight the finding row.
  useEffect(() => {
    if (!focusFindingId || !findings) return;
    setFilterPriority(null);
    setFilterStatus('all');
    setFilterCategory(null);
    setHighlightFindingId(focusFindingId);
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-finding-row="${focusFindingId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onFocusConsumed && onFocusConsumed();
    }, 80);
    const clear = setTimeout(() => setHighlightFindingId(null), 2500);
    return () => {
      clearTimeout(t);
      clearTimeout(clear);
    };
  }, [focusFindingId, findings, onFocusConsumed]);

  async function remove(f) {
    if (!window.confirm(`Delete Finding #${f.number}?`)) return;
    if (!online) {
      await enqueue({ type: 'delete_finding', finding_id: f.id });
      toast('Queued for sync');
      setFindings((arr) => arr.filter((x) => x.id !== f.id));
      return;
    }
    try {
      await api.delete(`/findings/${f.id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (findings === null) return <Spinner label="Loading findings…" />;

  const counts = { P1: 0, P2: 0, P3: 0 };
  findings.forEach((f) => (counts[f.priority] = (counts[f.priority] || 0) + 1));
  // Category counts for the at-a-glance tile bar
  const categoryCounts = {};
  findings.forEach((f) => {
    if (f.category) {
      categoryCounts[f.category] = (categoryCounts[f.category] || 0) + 1;
    }
  });
  const filtered = findings.filter((f) => {
    if (filterPriority && f.priority !== filterPriority) return false;
    if (filterStatus === 'open' && f.is_resolved) return false;
    if (filterStatus === 'resolved' && !f.is_resolved) return false;
    if (filterStatus === 'carryover' && !f.is_carryover) return false;
    if (filterCategory && f.category !== filterCategory) return false;
    return true;
  });

  return (
    <div>
      {capture && (
        <FindingCaptureModal
          eng={eng}
          initial={capture === 'new' ? null : capture}
          onClose={() => setCapture(null)}
          onSaved={() => {
            setCapture(null);
            load();
          }}
          onOpenFull={() => {
            const f = capture === 'new' ? 'new' : capture;
            setCapture(null);
            setEditing(f);
          }}
        />
      )}
      {editing ? (
        <FindingForm
          eng={eng}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setFilterPriority(filterPriority === 'P1' ? null : 'P1')}
                className="gl-chip gl-chip-p1"
                style={{ opacity: !filterPriority || filterPriority === 'P1' ? 1 : 0.4, cursor: 'pointer' }}
                data-testid="filter-p1"
              >
                P1 · {counts.P1}
              </button>
              <button
                type="button"
                onClick={() => setFilterPriority(filterPriority === 'P2' ? null : 'P2')}
                className="gl-chip gl-chip-p2"
                style={{ opacity: !filterPriority || filterPriority === 'P2' ? 1 : 0.4, cursor: 'pointer' }}
                data-testid="filter-p2"
              >
                P2 · {counts.P2}
              </button>
              <button
                type="button"
                onClick={() => setFilterPriority(filterPriority === 'P3' ? null : 'P3')}
                className="gl-chip gl-chip-p3"
                style={{ opacity: !filterPriority || filterPriority === 'P3' ? 1 : 0.4, cursor: 'pointer' }}
                data-testid="filter-p3"
              >
                P3 · {counts.P3}
              </button>
              <span className="gl-chip" style={{ background: '#1f2937', color: '#fff' }}>
                {filtered.length === findings.length ? 'TOTAL' : 'SHOWING'} · {filtered.length}{filtered.length !== findings.length && ` of ${findings.length}`}
              </span>
            </div>
            <button className="gl-btn gl-btn-accent" onClick={() => setCapture('new')} data-testid={TEST_IDS.addFindingBtn}>
              <PlusIcon size={20} weight="bold" /> Add Finding
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {['all', 'open', 'resolved', 'carryover'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilterStatus(s)}
                className="px-2 h-8 border-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                style={{
                  borderColor: filterStatus === s ? 'var(--gl-navy)' : '#d1d5db',
                  background: filterStatus === s ? 'var(--gl-navy)' : '#fff',
                  color: filterStatus === s ? '#fff' : 'var(--gl-dark)',
                  fontFamily: 'Inter',
                  letterSpacing: '0.08em',
                }}
                data-testid={`filter-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* GL-SPEC-APP-012 — Categories at a Glance tile bar.
              Tap any tile to filter the findings list to just that category. */}
          {Object.keys(categoryCounts).length > 0 && (
            <div className="mb-5">
              <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter' }}>
                Categories at a Glance
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(categoryCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, n]) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setFilterCategory(filterCategory === cat ? null : cat)}
                      className="px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-widest border-2 transition-all"
                      style={{
                        background: filterCategory === cat ? 'var(--gl-dark)' : '#fafaf7',
                        color: filterCategory === cat ? 'var(--gl-gold)' : 'var(--gl-dark)',
                        borderColor: filterCategory === cat ? 'var(--gl-gold)' : '#e5e3da',
                        fontFamily: 'Inter',
                      }}
                      data-testid={`category-tile-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {cat} <span className="ml-1 px-1.5 py-0.5 rounded-sm" style={{ background: filterCategory === cat ? 'var(--gl-gold)' : 'var(--gl-dark)', color: filterCategory === cat ? 'var(--gl-dark)' : '#fff' }}>{n}</span>
                    </button>
                  ))}
                {filterCategory && (
                  <button
                    type="button"
                    onClick={() => setFilterCategory(null)}
                    className="px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-widest border-2"
                    style={{ background: 'var(--gl-p1)', color: '#fff', borderColor: 'var(--gl-p1)', fontFamily: 'Inter' }}
                    data-testid="category-clear"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            </div>
          )}

          {findings.length === 0 ? (
            <EmptyState
              icon={ListChecksIcon}
              title="No findings yet"
              body="Capture observed hazards with priority, CFR citation, and photos."
              action={
                <button className="gl-btn gl-btn-primary" onClick={() => setCapture('new')}>
                  <PlusIcon size={18} weight="bold" /> Add First Finding
                </button>
              }
            />
          ) : filtered.length === 0 ? (
            <div className="gl-card text-center py-6 text-sm" style={{ color: 'var(--gl-muted)' }}>
              No findings match the active filter.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map((f) => (
                <FindingRow
                  key={f.id}
                  f={f}
                  isExpanded={false}
                  onToggleInline={() => setCapture(f)}
                  onOpenFull={() => setEditing(f)}
                  onDelete={() => remove(f)}
                  onJumpToDoc={onJumpToDoc}
                  highlight={highlightFindingId === f.id}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FindingRow({ f, onToggleInline, onOpenFull, onDelete, onJumpToDoc, isExpanded = false, highlight = false }) {
  return (
    <div
      className="gl-card gl-card-accent flex gap-4 items-start cursor-pointer hover:border-gray-400 transition-shadow duration-300"
      onClick={onToggleInline}
      data-testid={TEST_IDS.findingCard(f.id)}
      data-finding-row={f.id}
      style={{
        ...(highlight ? { boxShadow: '0 0 0 3px rgba(201, 168, 76, 0.5)', borderColor: 'var(--gl-gold)' } : {}),
        ...(isExpanded ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}),
      }}
    >
      <div className="flex flex-col items-center gap-1 min-w-[3.5rem]">
        <div
          className="font-display font-black text-3xl leading-none"
          style={{ color: 'var(--gl-dark)' }}
        >
          #{f.number}
        </div>
        <PriorityChip value={f.priority} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold uppercase text-lg leading-tight" style={{ color: 'var(--gl-dark)' }}>
            {f.violation_type || 'Untitled finding'}
          </span>
          {f.is_carryover && <CarryoverChip />}
          {f.is_resolved && <ResolvedChip />}
          {f.doc_key && (
            <button
              type="button"
              onClick={() => onJumpToDoc && onJumpToDoc(f.doc_key)}
              className={`gl-tag ${f.auto_generated_from_doc ? 'gl-tag-gold' : ''}`}
              style={f.auto_generated_from_doc ? undefined : { background: '#f3f4f6', color: 'var(--gl-muted)' }}
              title={f.auto_generated_from_doc
                ? 'Auto-generated from the Docs tab. Click to jump to the source document. Edit this card to take manual ownership.'
                : 'Linked to a Docs tab document. Click to view source. You have manually edited it - subsequent doc-state changes will not overwrite.'}
              data-testid="finding-doc-link-badge"
            >
              {f.auto_generated_from_doc ? 'auto · docs' : 'from-docs'}
              <span aria-hidden="true" style={{ fontSize: '10px', lineHeight: 1 }}>→</span>
            </button>
          )}
          {f.status && f.status !== 'open' && (
            <TagPill
              tone={f.status === 'verified_closed' ? 'p3' : f.status === 'closed' ? 'neutral' : 'navy'}
              testid="finding-status-badge"
            >
              {f.status === 'in_progress' ? 'in-progress' : f.status === 'verified_closed' ? 'verified-closed' : f.status.replace(/_/g, '-')}
            </TagPill>
          )}
          {f.category && (
            <TagPill tone="neutral" testid="finding-category-badge">
              {String(f.category).toLowerCase().replace(/[\s/]+/g, '-')}
            </TagPill>
          )}
          {f.is_repeat_finding && (
            <TagPill tone="p2" testid="finding-repeat-badge" title="This finding repeats from a prior visit at this client">
              repeat
            </TagPill>
          )}
          {f.client_acknowledged && (
            <TagPill tone="p3" testid="finding-ack-badge" title={`Client acknowledged on ${(f.client_acknowledged_at || '').slice(0, 10)}`}>
              client-ack
            </TagPill>
          )}
          {f.corrected_during_visit && (
            <TagPill tone="p3" testid="finding-corrected-onsite-badge" title="Corrected during the consultant visit">
              corrected-on-site
            </TagPill>
          )}
          {f.location && (
            <TagPill tone="navy" testid="finding-location-badge">
              {f.location.toLowerCase()}
            </TagPill>
          )}
        </div>
        {f.cfr_citation && (
          <div
            className="gl-mono text-xs mt-1.5 font-semibold"
            style={{ color: 'var(--gl-navy)', letterSpacing: '0.01em' }}
          >
            {f.cfr_citation}
            {f.standard_name && (
              <span className="ml-2" style={{ fontFamily: 'Inter', fontWeight: 400, color: 'var(--gl-muted)', letterSpacing: 0 }}>
                · {f.standard_name}
              </span>
            )}
          </div>
        )}
        {f.description && (
          <div className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--gl-muted)' }}>
            {f.description}
          </div>
        )}
        <div className="mt-2 flex items-center gap-3 text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter' }}>
          <span className="inline-flex items-center gap-1">
            <CameraIcon size={14} weight="bold" /> {(f.photo_ids || []).length} photos
          </span>
          {f.video_url && (
            <span className="inline-flex items-center gap-1" style={{ color: 'var(--gl-navy)' }}>
              <VideoCameraIcon size={14} weight="bold" /> Video linked
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button
          className="gl-btn gl-btn-secondary !h-10 !px-3"
          onClick={onToggleInline}
          data-testid={`finding-quick-edit-${f.id}`}
          aria-label="Quick edit finding"
          title="Quick edit (capture sheet)"
        >
          <PencilSimpleIcon size={18} weight="bold" />
        </button>
        <button
          className="gl-btn gl-btn-secondary !h-10 !px-3 hidden md:inline-flex"
          onClick={onOpenFull}
          data-testid={`finding-full-edit-${f.id}`}
          aria-label="Open full editor"
          title="Open full editor (photos, CFR lookup, repeat-finding link)"
        >
          <ArrowsOutSimpleIcon size={18} weight="bold" />
        </button>
        <button
          className="gl-btn gl-btn-secondary !h-10 !px-3"
          onClick={onDelete}
          data-testid={TEST_IDS.finDelete(f.id)}
          aria-label="Delete finding"
        >
          <TrashIcon size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function FindingForm({ eng, initial, onClose, onSaved }) {
  const online = useOnline();
  const [form, setForm] = useState({
    priority: initial?.priority || 'P2',
    violation_type: initial?.violation_type || '',
    cfr_citation: initial?.cfr_citation || '',
    standard_name: initial?.standard_name || '',
    penalty_exposure: initial?.penalty_exposure || '',
    description: initial?.description || '',
    corrective_action: initial?.corrective_action || '',
    corrective_due_date: initial?.corrective_due_date || '',
    internal_notes: initial?.internal_notes || '',
    video_url: initial?.video_url || '',
    photo_ids: initial?.photo_ids || [],
    is_resolved: !!initial?.is_resolved,
    // GL-SPEC-APP-009 - lifecycle status
    status: initial?.status || 'open',
    // GL-SPEC-APP-010 - repeat finding link
    is_repeat_finding: !!initial?.is_repeat_finding,
    prior_engagement_id: initial?.prior_engagement_id || '',
    prior_finding_number: initial?.prior_finding_number || '',
    // GL-SPEC-APP-011 - client acknowledgement
    client_acknowledged: !!initial?.client_acknowledged,
    // GL-SPEC-APP-012 - finding category
    category: initial?.category || '',
    // GL-SPEC-APP-014 - linked doc
    doc_key: initial?.doc_key || '',
    // GL-SPEC-APP-015/016/017 + Suggested Responsible Party
    location: initial?.location || '',
    exposure_impact: initial?.exposure_impact || '',
    corrected_during_visit: !!initial?.corrected_during_visit,
    suggested_responsible_party: initial?.suggested_responsible_party || '',
  });
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]); // [{id, caption, ...}]
  const [cfrOpen, setCfrOpen] = useState(false);
  const [cfrInput, setCfrInput] = useState('');
  const [cfrBusy, setCfrBusy] = useState(false);
  const [cfrResult, setCfrResult] = useState(null);
  // Lookup tables loaded once
  const [categories, setCategories] = useState([]);
  const [priorEngagements, setPriorEngagements] = useState([]);
  const [priorFindings, setPriorFindings] = useState([]);
  const [docCatalog, setDocCatalog] = useState([]);

  useEffect(() => {
    // Load categories, prior engagements for this client, and the docs catalog
    async function loadRefs() {
      try {
        const [c, pe, cov] = await Promise.all([
          api.get('/findings/categories').then((r) => r.data.categories).catch(() => []),
          api.get(`/engagements/${eng.id}/prior_engagements`).then((r) => r.data).catch(() => []),
          api.get(`/engagements/${eng.id}/doc_coverage`).then((r) => r.data).catch(() => null),
        ]);
        setCategories(c);
        setPriorEngagements(pe);
        // Flatten docs from buckets for the dropdown
        const docs = [];
        if (cov && cov.buckets) {
          cov.buckets.forEach((b) => {
            (b.docs || []).forEach((d) => docs.push({ key: d.key, label: d.label, bucket: b.label }));
            (b.not_applicable || []).forEach((d) => docs.push({ key: d.key, label: d.label, bucket: b.label }));
          });
        }
        setDocCatalog(docs);
      } catch (_) { /* fall through */ }
    }
    loadRefs();
  }, [eng.id]);

  useEffect(() => {
    // When prior engagement changes, load its findings for the prior-finding picker
    async function loadPriorFindings() {
      if (!form.prior_engagement_id) {
        setPriorFindings([]);
        return;
      }
      try {
        const { data } = await api.get(`/engagements/${form.prior_engagement_id}/findings_index`);
        setPriorFindings(data);
      } catch (_) {
        setPriorFindings([]);
      }
    }
    loadPriorFindings();
  }, [form.prior_engagement_id]);

  useEffect(() => {
    async function loadPhotos() {
      const ids = form.photo_ids || [];
      if (!ids.length) {
        setPhotos([]);
        return;
      }
      try {
        const results = await Promise.all(ids.map((id) => api.get(`/media/${id}`).then((r) => r.data).catch(() => null)));
        setPhotos(results.filter(Boolean));
      } catch (_) {
        setPhotos([]);
      }
    }
    loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.photo_ids?.length]);

  function up(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!online) {
      toast.error('Photo upload requires connectivity. Reconnect to capture.');
      return;
    }
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('caption', '');
      try {
        const { data } = await api.post(`/engagements/${eng.id}/media`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setForm((f) => ({ ...f, photo_ids: [...(f.photo_ids || []), data.id] }));
        setPhotos((p) => [...p, data]);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    e.target.value = '';
  }

  function removePhoto(id) {
    setForm((f) => ({ ...f, photo_ids: (f.photo_ids || []).filter((x) => x !== id) }));
    setPhotos((p) => p.filter((x) => x.id !== id));
  }

  async function setCaption(id, caption) {
    // Update locally; backend caption is set at upload time. For simplicity we patch media directly via the upload model.
    setPhotos((p) => p.map((x) => (x.id === id ? { ...x, caption } : x)));
  }

  async function save() {
    if (!form.violation_type.trim() && !form.description.trim()) {
      toast.error('Add a violation type or description');
      return;
    }
    setBusy(true);
    try {
      // Coerce empty-string optional-int fields to null so Pydantic accepts them.
      const payload = {
        ...form,
        prior_finding_number: form.prior_finding_number === '' || form.prior_finding_number === null
          ? null
          : Number(form.prior_finding_number),
        prior_engagement_id: form.prior_engagement_id || null,
        doc_key: form.doc_key || null,
      };
      if (initial) {
        if (!online) {
          await enqueue({ type: 'update_finding', finding_id: initial.id, payload });
          toast('Saved offline · will sync');
        } else {
          await api.patch(`/findings/${initial.id}`, payload);
        }
      } else {
        if (!online) {
          await enqueue({ type: 'create_finding', engagement_id: eng.id, payload });
          toast('Saved offline · will sync');
        } else {
          await api.post(`/engagements/${eng.id}/findings`, payload);
        }
      }
      flushQueue();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function runCfr() {
    if (!cfrInput.trim()) return;
    setCfrBusy(true);
    setCfrResult(null);
    try {
      const { data } = await api.post('/ai/cfr_lookup', { hazard_description: cfrInput });
      setCfrResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCfrBusy(false);
    }
  }

  function applyCfr() {
    if (!cfrResult) return;
    setForm((f) => ({
      ...f,
      cfr_citation: cfrResult.cfr_citation || f.cfr_citation,
      standard_name: cfrResult.standard_name || f.standard_name,
      penalty_exposure: cfrResult.penalty_exposure || f.penalty_exposure,
      corrective_action: cfrResult.corrective_action || f.corrective_action,
    }));
    setCfrOpen(false);
    setCfrInput('');
    setCfrResult(null);
    toast.success('CFR details applied');
  }

  return (
    <div className="gl-card gl-card-accent max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-extrabold uppercase text-2xl" style={{ color: 'var(--gl-dark)' }}>
          {initial ? `Edit Finding #${initial.number}` : 'New Finding'}
        </div>
        <button className="gl-btn gl-btn-secondary !h-10 !px-3" onClick={onClose} data-testid={TEST_IDS.finCancel}>
          <XIcon size={18} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="gl-label">Priority</div>
          <div className="flex gap-2" data-testid={TEST_IDS.finPriority}>
            {PRIORITIES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => up('priority', p)}
                className={`flex-1 gl-chip ${p === 'P1' ? 'gl-chip-p1' : p === 'P2' ? 'gl-chip-p2' : 'gl-chip-p3'}`}
                style={{
                  padding: '0.875rem 0',
                  fontSize: '0.95rem',
                  opacity: form.priority === p ? 1 : 0.45,
                  outline: form.priority === p ? '2px solid var(--gl-dark)' : 'none',
                }}
                data-testid={`priority-btn-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="gl-label">Violation Type</div>
          <input
            className="gl-input"
            value={form.violation_type}
            onChange={(e) => up('violation_type', e.target.value)}
            placeholder="Unguarded rotating machinery"
            data-testid={TEST_IDS.finViolationType}
          />
        </div>

        {/* GL-SPEC-APP-009 - Status / GL-SPEC-APP-012 - Category / GL-SPEC-APP-014 - Linked Doc */}
        <div>
          <div className="gl-label">Status</div>
          <select
            className="gl-input"
            value={form.status}
            onChange={(e) => up('status', e.target.value)}
            data-testid="finding-status-select"
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
            <option value="verified_closed">Verified Closed</option>
          </select>
        </div>
        <div>
          <div className="gl-label">Category</div>
          <select
            className="gl-input"
            value={form.category}
            onChange={(e) => up('category', e.target.value)}
            data-testid="finding-category-select"
          >
            <option value="">- pick a category -</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <div className="gl-label">Linked Doc <span className="text-xs" style={{ color: 'var(--gl-muted)' }}>(optional)</span></div>
          <select
            className="gl-input"
            value={form.doc_key}
            onChange={(e) => up('doc_key', e.target.value)}
            data-testid="finding-doc-link-select"
          >
            <option value="">- not linked -</option>
            {docCatalog.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label.slice(0, 55)}
              </option>
            ))}
          </select>
        </div>

        {/* GL-SPEC-APP-010 - Repeat Finding + GL-SPEC-APP-011 - Client Acknowledged */}
        <div className="md:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-sm" style={{ background: '#fafaf7', border: '1px solid #e5e3da' }}>
            {/* Repeat finding */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_repeat_finding}
                  onChange={(e) => up('is_repeat_finding', e.target.checked)}
                  data-testid="finding-repeat-toggle"
                />
                <span className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--gl-dark)' }}>
                  Repeat Finding
                </span>
              </label>
              {form.is_repeat_finding && (
                <div className="mt-2 space-y-2">
                  <select
                    className="gl-input !text-xs"
                    value={form.prior_engagement_id}
                    onChange={(e) => up('prior_engagement_id', e.target.value)}
                    data-testid="finding-prior-engagement-select"
                  >
                    <option value="">- pick a prior visit -</option>
                    {priorEngagements.length === 0 && (
                      <option disabled>No prior engagements for this client</option>
                    )}
                    {priorEngagements.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.visit_date || p.created_at?.slice(0, 10) || 'unknown'} - {p.type} ({p.finding_count} findings)
                      </option>
                    ))}
                  </select>
                  {form.prior_engagement_id && (
                    <select
                      className="gl-input !text-xs"
                      value={form.prior_finding_number || ''}
                      onChange={(e) => up('prior_finding_number', e.target.value ? parseInt(e.target.value, 10) : null)}
                      data-testid="finding-prior-number-select"
                    >
                      <option value="">- which finding from that visit? -</option>
                      {priorFindings.map((f) => (
                        <option key={f.number} value={f.number}>
                          #{f.number} [{f.priority || 'P3'}] {(f.violation_type || '(no title)').slice(0, 40)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
            {/* Client acknowledged */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.client_acknowledged}
                  onChange={(e) => up('client_acknowledged', e.target.checked)}
                  data-testid="finding-client-ack-toggle"
                />
                <span className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--gl-dark)' }}>
                  Client Acknowledged
                </span>
              </label>
              {form.client_acknowledged && initial?.client_acknowledged_at && (
                <div className="mt-1 text-xs" style={{ color: 'var(--gl-muted)' }}>
                  Acknowledged on {initial.client_acknowledged_at.slice(0, 10)}
                </div>
              )}
              {form.client_acknowledged && !initial?.client_acknowledged_at && (
                <div className="mt-1 text-xs" style={{ color: 'var(--gl-navy)' }}>
                  Timestamp will be set when you save.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="gl-label !mb-0">CFR Citation & Standard</div>
            <button
              type="button"
              className="gl-btn gl-btn-accent !h-10 !px-4 !text-xs"
              onClick={() => setCfrOpen((o) => !o)}
              disabled={!online}
              title={online ? 'CFR lookup' : 'Reconnect to look up CFR'}
              data-testid={TEST_IDS.cfrLookupBtn}
            >
              <SparkleIcon size={16} weight="fill" /> {online ? 'CFR Lookup' : 'Offline'}
            </button>
          </div>

          {cfrOpen && (
            <div className="mb-3 p-3 border-2 rounded-sm" style={{ borderColor: 'var(--gl-navy)', background: '#eff4fb' }}>
              <div className="gl-label">Describe the hazard in plain English</div>
              <textarea
                className="gl-input gl-textarea"
                rows={2}
                value={cfrInput}
                onChange={(e) => setCfrInput(e.target.value)}
                placeholder="Worker on second floor with no guardrails on roof edge"
                data-testid={TEST_IDS.cfrLookupInput}
              />
              <div className="mt-2 flex gap-2 flex-wrap">
                <button className="gl-btn gl-btn-primary !h-11 !px-4" onClick={runCfr} disabled={cfrBusy} data-testid={TEST_IDS.cfrLookupSubmit}>
                  {cfrBusy ? 'Asking…' : 'Look Up'}
                </button>
                {cfrResult && (
                  <button className="gl-btn gl-btn-accent !h-11 !px-4" onClick={applyCfr} data-testid={TEST_IDS.cfrLookupApply}>
                    Apply to Finding
                  </button>
                )}
              </div>
              {cfrResult && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm bg-white p-3 border border-gray-300 rounded-sm">
                  <div><b>CFR:</b> {cfrResult.cfr_citation}</div>
                  <div><b>Standard:</b> {cfrResult.standard_name}</div>
                  <div><b>Penalty:</b> {cfrResult.penalty_exposure}</div>
                  <div className="md:col-span-2"><b>Corrective Action:</b> {cfrResult.corrective_action}</div>
                  <div className="md:col-span-2 text-xs" style={{ color: 'var(--gl-muted)' }}><b>Rationale:</b> {cfrResult.rationale}</div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              className="gl-input"
              value={form.cfr_citation}
              onChange={(e) => up('cfr_citation', e.target.value)}
              placeholder="29 CFR 1910.23(b)(1)"
              data-testid={TEST_IDS.finCfrCitation}
            />
            <input
              className="gl-input"
              value={form.standard_name}
              onChange={(e) => up('standard_name', e.target.value)}
              placeholder="Fall Protection — Guardrails"
              data-testid={TEST_IDS.finStandardName}
            />
            <input
              className="gl-input"
              value={form.penalty_exposure}
              onChange={(e) => up('penalty_exposure', e.target.value)}
              placeholder="$5,000 – $16,131 (Serious)"
              data-testid={TEST_IDS.finPenalty}
            />
          </div>
        </div>

        {/* GL-SPEC-APP-015 — Location/Area */}
        <div className="md:col-span-3">
          <div className="gl-label">Location / Area</div>
          <input
            className="gl-input"
            value={form.location}
            onChange={(e) => up('location', e.target.value)}
            placeholder="North production bay - DEC Roller station"
            data-testid="finding-location-input"
          />
        </div>

        <div className="md:col-span-3">
          <div className="gl-label">Observed Condition</div>
          <textarea
            className="gl-input gl-textarea"
            rows={3}
            value={form.description}
            onChange={(e) => up('description', e.target.value)}
            placeholder="What you observed at the facility, factually stated."
            data-testid={TEST_IDS.finDescription}
          />
        </div>

        {/* GL-SPEC-APP-016 — Exposure / Potential Impact */}
        <div className="md:col-span-3">
          <div className="gl-label">Exposure / Potential Impact <span className="text-xs" style={{ color: 'var(--gl-muted)' }}>(one sentence, plain language)</span></div>
          <input
            className="gl-input"
            value={form.exposure_impact}
            onChange={(e) => up('exposure_impact', e.target.value)}
            placeholder="Worker amputation risk if guard fails during operation."
            data-testid="finding-exposure-input"
          />
        </div>

        <div className="md:col-span-3">
          <div className="gl-label">Recommended Corrective Action</div>
          <textarea
            className="gl-input gl-textarea"
            rows={3}
            value={form.corrective_action}
            onChange={(e) => up('corrective_action', e.target.value)}
            placeholder="Recommended fix in plain language."
            data-testid={TEST_IDS.finCorrective}
          />
        </div>

        {/* Suggested Responsible Party (owner) */}
        <div className="md:col-span-2">
          <div className="gl-label">Suggested Responsible Party</div>
          <input
            className="gl-input"
            value={form.suggested_responsible_party}
            onChange={(e) => up('suggested_responsible_party', e.target.value)}
            placeholder="Safety manager / Maintenance lead / Kevin Stutts"
            data-testid="finding-responsible-input"
          />
        </div>

        {/* GL-SPEC-APP-017 — Corrected During Visit toggle */}
        <div>
          <div className="gl-label">Corrected On-Site?</div>
          <label className="flex items-center gap-2 cursor-pointer mt-2 px-3 py-2 border-2 rounded-sm" style={{ borderColor: form.corrected_during_visit ? 'var(--gl-p3)' : '#e5e3da', background: form.corrected_during_visit ? '#e6f4ec' : '#fff' }}>
            <input
              type="checkbox"
              checked={form.corrected_during_visit}
              onChange={(e) => up('corrected_during_visit', e.target.checked)}
              data-testid="finding-corrected-onsite-toggle"
            />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: form.corrected_during_visit ? 'var(--gl-p3)' : 'var(--gl-muted)' }}>
              {form.corrected_during_visit ? 'Yes - corrected on the spot' : 'No - still open'}
            </span>
          </label>
        </div>

        <div className="md:col-span-3">
          <div className="gl-label">Suggested Completion Timeframe</div>
          {/* Quick-select chips */}
          <div className="flex flex-wrap gap-2 mb-2">
            {[
              { label: 'Immediate', days: 0 },
              { label: '24 hours', days: 1 },
              { label: '7 days', days: 7 },
              { label: '30 days', days: 30 },
              { label: '60 days', days: 60 },
              { label: '90 days', days: 90 },
              { label: 'Next visit', days: null },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => {
                  if (opt.days === null) {
                    up('corrective_due_date', 'Next visit');
                  } else if (opt.days === 0) {
                    up('corrective_due_date', new Date().toISOString().slice(0, 10));
                  } else {
                    const d = new Date();
                    d.setDate(d.getDate() + opt.days);
                    up('corrective_due_date', d.toISOString().slice(0, 10));
                  }
                }}
                className="px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-sm border-2 hover:bg-amber-50"
                style={{ borderColor: 'var(--gl-gold)', color: 'var(--gl-dark)', fontFamily: 'Inter' }}
                data-testid={`finding-timeframe-${opt.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="gl-input"
            value={form.corrective_due_date || ''}
            onChange={(e) => up('corrective_due_date', e.target.value)}
            placeholder="YYYY-MM-DD or free-form (e.g., Next visit)"
            data-testid="fin-corrective-due-date"
          />
          <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>Shown on PDF + report deliverable.</div>
        </div>
        <div className="md:col-span-2">
          <div className="gl-label">Internal Notes (NOT sent to client)</div>
          <textarea
            className="gl-input gl-textarea"
            rows={2}
            value={form.internal_notes || ''}
            onChange={(e) => up('internal_notes', e.target.value)}
            placeholder="Your private context — contact left mid-walk, follow-up reminder, etc."
            data-testid="fin-internal-notes"
            style={{ background: '#fffbeb', borderColor: 'var(--gl-gold)' }}
          />
        </div>

        <div className="md:col-span-3">
          <div className="gl-label">Video URL (optional, QR-coded in PDF)</div>
          <input
            className="gl-input"
            value={form.video_url}
            onChange={(e) => up('video_url', e.target.value)}
            placeholder="https://video.example.com/walkthrough-123"
            data-testid={TEST_IDS.finVideoUrl}
          />
        </div>

        {/* Photos */}
        <div className="md:col-span-3">
          <div className="gl-label">Photos</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative border-2 border-gray-300 rounded-sm overflow-hidden">
                <img
                  src={mediaUrl(p.id)}
                  alt={p.caption || ''}
                  className="w-full h-32 object-cover"
                />
                <button
                  className="absolute top-1 right-1 bg-white border-2 border-[var(--gl-p1)] text-[var(--gl-p1)] rounded-sm p-1"
                  onClick={() => removePhoto(p.id)}
                  aria-label="Remove photo"
                  data-testid={`photo-remove-${p.id}`}
                >
                  <XIcon size={14} weight="bold" />
                </button>
                <input
                  className="block w-full px-2 py-1 text-xs border-t border-gray-200 outline-none"
                  placeholder="Caption…"
                  defaultValue={p.caption || ''}
                  onBlur={(e) => setCaption(p.id, e.target.value)}
                />
              </div>
            ))}
            <div className="flex gap-2 h-32">
              {/* Library / Files - no capture attribute = full iOS picker */}
              <label className="flex-1 flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-sm cursor-pointer hover:bg-gray-50" style={{ borderColor: 'var(--gl-navy)', color: 'var(--gl-navy)' }}>
                <ImageIcon size={26} weight="bold" />
                <span className="text-xs font-bold uppercase tracking-widest text-center px-1" style={{ fontFamily: 'Inter' }}>
                  Photo Library
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onUpload}
                  data-testid={TEST_IDS.finPhotoUpload}
                />
              </label>
              {/* Camera - capture forces the camera UI */}
              <label className="flex-1 flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-sm cursor-pointer hover:bg-gray-50" style={{ borderColor: 'var(--gl-dark)', color: 'var(--gl-dark)', background: '#fafaf7' }}>
                <CameraIcon size={26} weight="bold" />
                <span className="text-xs font-bold uppercase tracking-widest text-center px-1" style={{ fontFamily: 'Inter' }}>
                  Take Photo
                </span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={onUpload}
                  data-testid="finding-photo-camera"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 flex gap-3 flex-wrap items-center">
        {initial && (
          <label className="flex items-center gap-2 px-3 py-2 border-2 rounded-sm cursor-pointer"
            style={{ borderColor: form.is_resolved ? 'var(--gl-p3)' : '#d1d5db', background: form.is_resolved ? '#ecfdf5' : '#fff' }}
          >
            <input
              type="checkbox"
              className="w-5 h-5"
              checked={form.is_resolved}
              onChange={(e) => up('is_resolved', e.target.checked)}
              data-testid="fin-is-resolved"
            />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ fontFamily: 'Inter', letterSpacing: '0.1em', color: 'var(--gl-dark)' }}>
              Mark as Resolved
            </span>
          </label>
        )}
        <button className="gl-btn gl-btn-primary" onClick={save} disabled={busy} data-testid={TEST_IDS.finSubmit}>
          {busy ? 'Saving…' : initial ? 'Save Finding' : 'Add Finding'}
        </button>
        <button className="gl-btn gl-btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
